import { motion, AnimatePresence } from 'framer-motion';

interface SessionExpiredModalProps {
  isOpen: boolean;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

export const SessionExpiredModal = ({
  isOpen,
  onLogout,
  isLoggingOut = false,
}: SessionExpiredModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[201] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-3 isolate bg-[#C2CCDF] border-2 border-[rgba(46,64,86,0.2)] rounded-2xl pointer-events-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Content */}
              <div className="flex flex-col items-center gap-4 w-full max-w-[307px]">
                {/* Warning Icon Container - Overlapping */}
                <motion.div
                  className="absolute top-7 z-10"
                  initial={{ scale: 0 }}
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    scale: {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    },
                  }}
                >
                  <div className="text-5xl">⚠️</div>
                </motion.div>

                {/* Message Container */}
                <div className="flex flex-col items-center pt-16 pb-4 px-4 w-full bg-[#8391A6] rounded-xl gap-3">
                  <h2 className="font-lilita text-[24px] text-white text-center [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                    Session Expired
                  </h2>
                  <p className="font-lilita text-[16px] text-white/90 text-center leading-relaxed [text-shadow:0.5px_1px_0px_#000]">
                    Your wallet session has expired. You must reconnect to
                    continue playing.
                  </p>
                </div>

                {/* Logout Button */}
                <motion.button
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  className="relative flex items-center justify-center px-6 py-3 gap-3 isolate h-14 border-2 border-black rounded-xl overflow-hidden w-full"
                  style={{
                    background:
                      'linear-gradient(135deg, #FF0000 0%, #D70000 49.04%, #FF6F6F 100%)',
                    opacity: isLoggingOut ? 0.7 : 1,
                    cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  }}
                  whileHover={!isLoggingOut ? { scale: 1.05, y: -2 } : {}}
                  whileTap={!isLoggingOut ? { scale: 0.95 } : {}}
                  transition={{ duration: 0.15 }}
                >
                  <div className="absolute inset-x-2 top-2 h-8 bg-white/30 rounded-lg z-0" />
                  <span className="font-lilita text-[24px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                    {isLoggingOut ? 'Logging out...' : 'Logout & Reconnect'}
                  </span>
                </motion.button>

                {/* Helper Text */}
                <p className="text-center text-white text-sm -mt-2 mb-1 [text-shadow:0.5px_1px_0px_#000]">
                  Commander, your progress is safe!
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
